import { useState, useEffect } from 'react';
import { useSubscribeData } from 'spectacle-ui-game-bridge-react';
import { DeviceType } from 'spectacle-ui-game-bridge-react';

type Offset = {
  top: number;
};

type Offsets = Record<string, Offset> | null;

const useFocusInputs = (): [any, React.CSSProperties | undefined, any, any] => {
  const device_os = useSubscribeData<string>('game', 'device_os');
  const [focusedOffsets, setFocusedOffsets] = useState<Offsets>(null);
  const [focusedElement, setFocusedElement] = useState<any>(null);
  const visualViewport = window?.visualViewport;
  const orientation = window?.screen?.orientation;
  const [offsetStyle, setOffsetStyle] = useState<React.CSSProperties | undefined>(undefined);

  // Assign visual viewport props
  useEffect(() => {
    let pendingUpdate = false;

    // Android
    if (focusedElement && focusedOffsets?.[focusedElement].top && device_os === DeviceType.Android) {
      setOffsetStyle({
        transform: `translateY(${focusedOffsets?.[focusedElement].top}px)`,
      });
    }

    const handleNavigator = () => {
      if (pendingUpdate) return;
      pendingUpdate = true;

      requestAnimationFrame(() => {
        pendingUpdate = false;

        // iOS shifts the viewport, so for this platform we use the visual viewport to find the offset
        if (focusedElement && device_os === DeviceType.iOS && visualViewport?.offsetTop && focusedOffsets?.[focusedElement].top) {
          setOffsetStyle({
            transform: `translateY(-${visualViewport.offsetTop - 100 + focusedOffsets?.[focusedElement].top}px)`,
          });
        }
      });
    };

    const handleKeyboardHide = () => {
      const activeElement = document.activeElement as HTMLElement;

      if (activeElement) {
        activeElement.blur();
      }
    };

    document.body.addEventListener('atvi_keyboard_dismiss', handleKeyboardHide);

    const handleOrientationChange = (e: Event) => {
      const activeElement = document.activeElement as HTMLElement;

      if (activeElement) {
        activeElement.blur();
      }
    };

    if (visualViewport) {
      visualViewport.addEventListener('resize', handleNavigator);
      visualViewport.addEventListener('scroll', handleNavigator);
    }

    if (orientation) {
      orientation.addEventListener('change', handleOrientationChange);
    }

    return () => {
      document.body.removeEventListener('atvi_keyboard_dismiss', handleKeyboardHide);

      if (visualViewport) {
        visualViewport.removeEventListener('resize', handleNavigator);
        visualViewport.removeEventListener('scroll', handleNavigator);
      }

      if (orientation) {
        orientation.removeEventListener('change', handleOrientationChange);
      }
    };
  }, [visualViewport, device_os, focusedElement, focusedOffsets, orientation]);

  const setOffset = (name: string, top: number) => {
    setFocusedOffsets((prev: any) => ({
      ...prev,
      [name]: { top: (top - 100) * -1 },
    }));
  };

  const onFocus = (e: any) => {
    e.preventDefault();

    setFocusedElement(e.currentTarget.name || e.currentTarget.dataset.title);
  };

  const onBlur = (e: Event | null) => {
    if (e) {
      e.preventDefault();
    }

    setFocusedElement(null);
    setOffsetStyle(undefined);
  };

  return [setOffset, offsetStyle, onFocus, onBlur];
};

export default useFocusInputs;
